import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';
import omit from 'lodash/omit';
import { useTranslation } from 'react-i18next';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import { history } from 'nallian-shared-ui/lib/config/history';
import { COMMUNITY_DETAIL_TABS } from '../../constants';
import updateCommunityMemberRolesService from '../../services/communityMemberRoles/updateCommunityMemberRolesService';
import createCommunityMemberService from '../../services/communityMembers/createCommunityMemberService';
import communityMembersService from '../../services/communityMembers/communityMembersService';
import CommunityMemberForm from './CommunityMemberForm';

const EMPTY_COMMUNITY_MEMBER = {};

const NewCommunityMember = ({
  open,
  dispatchCommunityMembersGet,
  dispatchCreateCommunityMemberPost,
  dispatchUpdateCommunityMemberRolesPost,
  createCommunityMemberFetch,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const handleSubmit = useCallback(
    async body => {
      await dispatchCreateCommunityMemberPost(
        { ...omit(body, 'communityRoles'), communityId: id },
        undefined, // no callback because we have another API call coming up
        () => history.push(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`),
        t
      );
      await dispatchUpdateCommunityMemberRolesPost(
        id,
        body.organisationId,
        {
          organisationRoles: body.communityRoles,
        },
        () => {
          showToaster('success', t('createCommunityMemberSuccess'));
          dispatchCommunityMembersGet(id);
          history.push(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`);
        },
        () => {
          showToaster('error', t('createCommunityMemberFailed'));
          history.push(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`);
        },
        t
      );
    },
    [
      dispatchCommunityMembersGet,
      dispatchCreateCommunityMemberPost,
      dispatchUpdateCommunityMemberRolesPost,
      id,
      t,
    ]
  );

  if (!open) return null;

  return (
    <CommunityMemberForm
      header={t('linkExistingOrganisation')}
      onSubmit={handleSubmit}
      submitProcessing={createCommunityMemberFetch.pending}
      returnRoute={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`}
      cancelRoute={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.MEMBERS}`}
      data={EMPTY_COMMUNITY_MEMBER}
      hasOrganisationIdField
    />
  );
};

NewCommunityMember.propTypes = {
  dispatchCommunityMembersGet: PropTypes.func.isRequired,
  dispatchCreateCommunityMemberPost: PropTypes.func.isRequired,
  dispatchUpdateCommunityMemberRolesPost: PropTypes.func.isRequired,
  createCommunityMemberFetch: PropTypes.object.isRequired,
  open: PropTypes.bool,
};

NewCommunityMember.defaultProps = { open: false };

export default compose(
  communityMembersService,
  updateCommunityMemberRolesService,
  createCommunityMemberService
)(NewCommunityMember);
